import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowDownRight, ArrowUpRight, Download, Mail, MessageCircle, Minus, RefreshCw } from "lucide-react";
import { api, copyText, downloadCsv, rp } from "@/lib/api";

const Delta = ({ value }) => {
  const Icon = !value ? Minus : value > 0 ? ArrowUpRight : ArrowDownRight;
  const cls = !value ? "text-ink-soft" : value > 0 ? "text-emerald-600" : "text-lm-red";
  return <span className={`inline-flex items-center gap-0.5 text-[11px] font-semibold ${cls}`}>
    <Icon size={12} />{Math.abs(value)}%</span>;
};

const Card = ({ label, value, sub, delta, testid }) => (
  <div className="bg-white border border-black/10 rounded-lg p-4" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-widest text-ink-soft">{label}</div>
    <div className="mt-1.5 text-xl md:text-2xl font-semibold tabular-nums">{value}</div>
    <div className="mt-1 flex items-center gap-2">
      {delta !== undefined && <Delta value={delta} />}
      {sub && <span className="text-[11px] text-ink-soft truncate">{sub}</span>}
    </div>
  </div>
);

export default function WeeklySummary() {
  const [d, setD] = useState(null);
  const [recent, setRecent] = useState([]);
  const [week, setWeek] = useState("");
  const [busy, setBusy] = useState("");

  const loadRecent = () => api.get("/weekly-summary/recent").then((r) => setRecent(r.data)).catch(() => {});
  const load = (ws) => api.get("/weekly-summary", { params: { week_start: ws || undefined } })
    .then((r) => { setD(r.data); setWeek(r.data.summary.week_start); })
    .catch((e) => toast.error(e?.response?.data?.detail || "Gagal memuat rekap"));
  useEffect(() => { load(); loadRecent(); }, []);

  const act = async (key, fn, msg) => {
    setBusy(key);
    try { await fn(); if (msg) toast.success(msg); loadRecent(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
    finally { setBusy(""); }
  };

  if (!d) return <div className="text-ink-soft">Memuat rekap mingguan…</div>;
  const s = d.summary;
  const maxDay = Math.max(1, ...s.daily.map((x) => x.revenue));

  return (
    <div className="space-y-6" data-testid="admin-weekly-summary">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl tracking-tight">Ringkasan Mingguan</h1>
          <p className="mt-2 text-sm text-ink-soft max-w-2xl">
            Rekap Senin–Minggu dibanding minggu sebelumnya. Dikirim otomatis ke
            {" "}{d.email_to || "email owner"} setiap <span className="text-ink font-medium">{d.schedule.label}</span>
            {" "}(bisa diubah di Settings).
          </p>
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          <input type="date" data-testid="weekly-date" value={week} onChange={(e) => load(e.target.value)}
            className="h-10 rounded-md border border-input bg-white px-3 text-sm" />
          <button data-testid="weekly-regenerate" disabled={busy === "gen"}
            onClick={() => act("gen", async () => {
              const r = await api.post("/weekly-summary/generate", { week_start: week });
              setD(r.data);
            }, "Rekap diperbarui")}
            className="h-10 px-4 rounded-md border border-black/15 text-sm inline-flex items-center gap-2">
            <RefreshCw size={14} /> Hitung ulang
          </button>
          <button data-testid="weekly-csv"
            onClick={() => downloadCsv("/weekly-summary/export.csv", { week_start: week },
              `ringkasan-mingguan-${week}.csv`).then(() => toast.success("CSV diunduh"))
              .catch(() => toast.error("Gagal mengunduh CSV"))}
            className="h-10 px-4 rounded-md border border-black/15 text-sm inline-flex items-center gap-2">
            <Download size={14} /> CSV
          </button>
          <button data-testid="weekly-email" disabled={busy === "mail" || !d.email_configured}
            onClick={() => act("mail", () => api.post("/weekly-summary/send-email", { week_start: week }),
              "Rekap dikirim ke email")}
            className="h-10 px-4 rounded-md border border-black/15 text-sm inline-flex items-center gap-2 disabled:opacity-50">
            <Mail size={14} /> Kirim email sekarang
          </button>
          <a data-testid="weekly-wa" href={d.wa_link} target="_blank" rel="noreferrer"
            className="h-10 px-4 rounded-md lm-btn-primary text-sm font-semibold inline-flex items-center gap-2">
            <MessageCircle size={14} /> Kirim ke WhatsApp
          </a>
        </div>
      </div>

      <div className="text-sm font-semibold" data-testid="weekly-label">
        {s.label} <span className="text-ink-soft font-normal">· dibanding {s.prev_label}</span>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card label="Revenue" value={rp(s.current.revenue)} delta={s.delta.revenue}
          sub={`sebelumnya ${rp(s.prev.revenue)}`} testid="weekly-revenue" />
        <Card label="Revenue Lunas" value={rp(s.current.paid_revenue)} delta={s.delta.paid_revenue}
          sub={`${s.current.paid_bookings} booking lunas`} testid="weekly-paid" />
        <Card label="Booking" value={s.current.bookings} delta={s.delta.bookings}
          sub={`${s.current.cancelled} dibatalkan`} testid="weekly-bookings" />
        <Card label="Unit Tersewa" value={s.current.units_rented} delta={s.delta.units_rented}
          sub={`rata-rata ${rp(s.current.avg_order_value)}/booking`} testid="weekly-units" />
        <Card label="Booking Baru Masuk" value={s.new_bookings} sub={rp(s.new_bookings_value)} testid="weekly-new" />
        <Card label="Customer Baru" value={s.new_customers} testid="weekly-customers" />
        <Card label="Deposit Dipegang" value={rp(s.deposits_collected)} testid="weekly-deposits" />
        <Card label="Minggu Ini" value={`${s.week_ahead.bookings} booking`}
          sub={`${s.week_ahead.units} unit · ${rp(s.week_ahead.revenue)}`} testid="weekly-ahead" />
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-white border border-black/10 rounded-lg p-4" data-testid="weekly-daily">
          <h2 className="text-sm font-semibold">Rincian harian
            {s.best_day && <span className="text-ink-soft font-normal"> · terbaik {s.best_day.day}</span>}
          </h2>
          <div className="mt-2 space-y-2">
            {s.daily.map((x) => (
              <div key={x.date} data-testid={`weekly-day-${x.date}`}>
                <div className="flex justify-between text-xs">
                  <span>{x.day} {x.date.slice(8)}</span>
                  <span className="text-ink-soft">{rp(x.revenue)} · {x.bookings} booking</span>
                </div>
                <div className="mt-1 h-1.5 rounded-full bg-black/5 overflow-hidden">
                  <div className="h-full rounded-full bg-lm-red/80 transition-all duration-500"
                    style={{ width: `${Math.max(2, (x.revenue / maxDay) * 100)}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-4">
          <div className="bg-white border border-black/10 rounded-lg p-4" data-testid="weekly-breakdown">
            <h2 className="text-sm font-semibold">Paket & cabang</h2>
            <div className="mt-2 divide-y divide-black/5 text-xs">
              {s.packages.length === 0 && <div className="py-2 text-ink-soft">Belum ada data.</div>}
              {s.packages.map((p) => (
                <div key={p.label} className="py-2 flex justify-between gap-3">
                  <span className="truncate">{p.label}</span>
                  <span className="text-ink-soft whitespace-nowrap">{rp(p.revenue)} · {p.bookings}x</span>
                </div>
              ))}
              {s.branches.map((b) => (
                <div key={b.key || b.label} className="py-2 flex justify-between gap-3">
                  <span className="truncate">{b.label} {b.code ? `(${b.code})` : ""}</span>
                  <span className="text-ink-soft whitespace-nowrap">{rp(b.revenue)} · {b.bookings}x</span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white border border-black/10 rounded-lg p-4" data-testid="weekly-text-box">
            <div className="flex items-center justify-between gap-3">
              <h2 className="text-sm font-semibold">Teks siap kirim</h2>
              <button data-testid="weekly-copy" className="text-xs px-3 py-1.5 rounded-md border border-black/15"
                onClick={async () => (await copyText(d.text) ? toast.success("Teks dicopy")
                  : toast.error("Gagal copy — pilih teksnya lalu copy manual"))}>Copy</button>
            </div>
            <pre className="mt-3 text-xs whitespace-pre-wrap leading-relaxed max-h-72 overflow-y-auto">{d.text}</pre>
          </div>
        </div>
      </div>

      <div className="bg-white border border-black/10 rounded-lg p-4" data-testid="weekly-history">
        <h2 className="text-sm font-semibold">Riwayat rekap</h2>
        <div className="mt-2 divide-y divide-black/5 text-xs">
          {recent.length === 0 && <div className="py-2 text-ink-soft">Belum ada riwayat.</div>}
          {recent.map((r) => (
            <button key={r.week_start} onClick={() => load(r.week_start)}
              data-testid={`weekly-history-${r.week_start}`}
              className="w-full py-2 flex items-center justify-between gap-3 text-left hover:text-lm-red">
              <span>{r.label || r.week_start}</span>
              <span className="text-ink-soft">{rp(r.current?.revenue || 0)} · {r.current?.bookings || 0} booking
                {r.email_sent_at ? " · email ✓" : ""}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
